import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { paths } from "../../../routes/paths";
import { useClaims } from "../../auth/useClaims";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useGuardedCustomerPayments } from "../useGuardedCustomerPayments";
import { useCanReadCollectorLists } from "../../collections/collectorListsApi";
import DashboardSectionCard from "../components/DashboardSectionCard";

const PAGE = 10;

const chipStyle = (color) => ({
  display: "inline-block",
  padding: "1px 8px",
  marginLeft: 4,
  fontSize: 11,
  fontWeight: 700,
  borderRadius: 999,
  color,
  border: `1px solid ${color}`,
  background: `${color}14`,
  whiteSpace: "nowrap",
});
const MISSED = "#dc2626";
const DUE = "#d97706";

// "Aug 2026" - the month and year of the last receipt, which is what the
// office asks first ("when did they last pay?"), not the exact day.
const monthYear = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { month: "short", year: "numeric" }) : null);

/**
 * Customers who hold active guard posts but have no collection entry for one or
 * more months - the recurring (monthly) side of "expected vs collected".
 *
 * Deliberately NOT an amount: a guarding contract's monthly value lives on the
 * sales order, and many long-standing customers predate Sales Orders entirely,
 * so an "outstanding" figure would be wrong for most of them. What IS reliable
 * for every guarded customer is "guards are posted there" and "did any money
 * come in that month" - so that is all this card claims.
 *
 * A finished month with nothing received is "missed" (red). The running month
 * is only "due" (amber) - it is not over, so it is not yet a missed payment.
 */
const GuardedCustomersMissedPaymentsCard = () => {
  // Whole-branch figures only: Attendance.View is what lifts the slot list out of
  // "my own areas", Collections.ViewAll what lifts the summary out of "my own
  // receipts". With less, every customer outside the caller's own slice would
  // show as unpaid - worse than not showing the card.
  const { permissions } = useClaims();
  const canView = permissions.includes("Attendance.View") && permissions.includes("Collections.ViewAll");

  // Last collected month + collector first name come from the collector lists
  // (Batch189). Their endpoint has its own read policy, so only ask when the
  // caller can get them - otherwise those two facts are simply not shown.
  const withCollectors = useCanReadCollectorLists();

  const { selectedBranchId } = useBranchFilter();
  const branchId = selectedBranchId ?? undefined;
  const { customers, months, isLoading, isError } = useGuardedCustomerPayments({ branchId, skip: !canView, withCollectors });
  const [showAll, setShowAll] = useState(false);

  const flagged = useMemo(
    () =>
      customers
        .filter((c) => c.missedMonths.length > 0 || c.isCurrentMonthDue)
        .sort(
          (a, b) =>
            b.missedMonths.length - a.missedMonths.length ||
            b.totalGuards - a.totalGuards ||
            String(a.custName || "").localeCompare(String(b.custName || ""))
        ),
    [customers]
  );

  if (!canView) return null;

  const missedCount = flagged.filter((c) => c.missedMonths.length > 0).length;
  const visible = showAll ? flagged : flagged.slice(0, PAGE);
  const currentMonth = months.find((m) => m.isCurrent);
  const firstMonth = months[0];

  return (
    <div className="col-12 col-xl-6">
      <DashboardSectionCard
        title="Guarded Customers - Payments Not Received"
        headerRight={firstMonth ? `Since ${firstMonth.label}` : undefined}
        isLoading={isLoading}
        isEmpty={!isError && flagged.length === 0}
        emptyText="Every customer with active guards has a payment entry for every month."
      >
        {isError ? (
          <p className="text-danger small mb-0">Could not load guard assignments or collections.</p>
        ) : (
          <>
            <p className="small text-muted mb-2">
              {customers.length} customers with active guards ·{" "}
              <span style={{ color: MISSED, fontWeight: 700 }}>{missedCount} missed a month</span> ·{" "}
              <span style={{ color: DUE, fontWeight: 700 }}>
                {flagged.length - missedCount} not yet paid{currentMonth ? ` for ${currentMonth.label}` : ""}
              </span>
            </p>

            <ul className="list-unstyled mb-0">
              {visible.map((c) => (
                <li key={c.key} className="d-flex justify-content-between align-items-start gap-2 py-2 border-bottom">
                  <div style={{ minWidth: 0 }}>
                    <Link
                      to={paths.collectionsFiltered({
                        branchId: c.branchId,
                        custId: c.custId,
                        custName: c.custName,
                        dateFrom: firstMonth?.dateFrom,
                        dateTo: currentMonth?.dateTo,
                      })}
                      className="fw-semibold text-decoration-none d-block text-truncate"
                      title={`See ${c.custName}'s collections since ${firstMonth?.label}`}
                    >
                      {c.custName || `Customer ${c.custId}`}
                    </Link>
                    <span className="small text-muted d-block">
                      {c.branchCode} · {c.totalGuards} guard{c.totalGuards === 1 ? "" : "s"} (Day {c.dayGuards} / Night {c.nightGuards})
                    </span>
                    {withCollectors && (
                      <span className="small d-block">
                        <span className="text-muted">Last paid </span>
                        {c.lastCollectedDate ? (
                          <span className="fw-semibold">{monthYear(c.lastCollectedDate)}</span>
                        ) : (
                          <span className="text-muted fst-italic">never (in our records)</span>
                        )}
                        <span className="text-muted"> · Collector </span>
                        {c.collectorFirstName ? (
                          <span className="fw-semibold">{c.collectorFirstName}</span>
                        ) : (
                          <span className="text-muted fst-italic">not on any list</span>
                        )}
                      </span>
                    )}
                  </div>
                  <div className="text-end" style={{ flexShrink: 0, maxWidth: "55%" }}>
                    {c.missedMonths.map((m) => (
                      <span key={m.key} style={chipStyle(MISSED)} title="No payment received in this month">
                        {m.label}
                      </span>
                    ))}
                    {c.isCurrentMonthDue && currentMonth && (
                      <span style={chipStyle(DUE)} title="Nothing received yet this month - the month is still running">
                        {currentMonth.label} due
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {flagged.length > PAGE && (
              <button type="button" className="btn btn-link btn-sm px-0 mt-2" onClick={() => setShowAll((v) => !v)}>
                {showAll ? "Show fewer" : `Show all ${flagged.length}`}
              </button>
            )}
          </>
        )}
      </DashboardSectionCard>
    </div>
  );
};

export default GuardedCustomersMissedPaymentsCard;
